import * as Factory from 'factory.ts'
import faker from 'faker'

import {
  type Bill,
  type BillCommon,
  type BillCost,
  type BillStatusBill,
  type BillStatusBillApprovalDelegatee,
  type BillStatusBillApprover,
} from '@webapp/models/Bill'
import { BillStatuses } from '@webapp/models/enums/BillStatus'
import { BillTypes } from '@webapp/models/enums/BillType'
import { CostAllocationMock } from './costallocation.factory'
import { CurrencyMock } from './currency.factory'
import { PaymentV2SimpleMock } from './paymentV2Simple.factory'
import { UserMock } from './user.factory'
import { SimpleVendorMock, VendorMock } from './vendor.factory'
import { VendorPaymentMethodMock } from './vendorPaymentMethod.factory'

export const BillCommonMock = Factory.Sync.makeFactory<BillCommon>({
  id: Factory.each(() => faker.random.number()),
  creator: UserMock.build(),
  currency: CurrencyMock.build(),
  due_date: Factory.each(() => faker.date.past().toISOString()),
  gl_post_date: Factory.each(() => faker.date.past().toISOString()),
  group: Factory.each(() => faker.random.number()),
  invoice_created: false,
  invoice_date: Factory.each(() => faker.date.past().toISOString()),
  invoice_number: Factory.each(() => faker.random.number().toString()),
  invoice_uuid: null,
  invoice_total: null,
  last_export_date: Factory.each(() => faker.date.past().toISOString()),
  last_export_user: Factory.each(() => UserMock.build()),
  last_modified_datetime: Factory.each(() => faker.date.past().toISOString()),
  locked: false,
  next_approver_choices: null,
  status: BillStatuses.DRAFT,
  submitted_date: Factory.each(() => faker.date.past().toISOString()),
  total_cost_with_tax: Factory.each(() => faker.random.number()),
  user: UserMock.build(),
  uuid: Factory.each(() => faker.random.uuid()),
  version: 1,
  type: BillTypes.ORDER,
  payment_date: null,
})

export const BillMock = Factory.Sync.makeFactory<Bill>({
  ...BillCommonMock.build(),
  vendor: VendorMock.build(),
  active: true,
  added_purchase_orders: [],
  approval_chain: null,
  approver: null,
  bill_payment: {
    amount: faker.finance.amount(),
    id: faker.random.number(),
    payment: PaymentV2SimpleMock.build(),
    payment_method: VendorPaymentMethodMock.build(),
    transaction: null,
  },
  bill_statuses: null,
  comments: [],
  converted_total_cost: 0,
  costs: [],
  creditcard: null,
  creditcard_name: null,
  duplicate_invoice_number: false,
  exclusive_tax_amount: 0,
  items: [],
  inclusive_tax_amount: 0,
  invoice_attachments: [],
  invoice_uuid: null,
  invoice_created: false,
  invoice_total: null,
  note: '',
  payment_method: 0,
  payment_method_name: '',
  payment_terms: '',
  subtotal_cost: 0,
  shipping_amount: 0,
  tax_amount: 0,
  total_cost: 0,
  vendor_address_one: null,
  vendor_address_two: null,
  vendor_city: null,
  vendor_contact: null,
  vendor_country: null,
  vendor_name: null,
  vendor_postal_code: null,
  vendor_state_province: null,
  version_choices: [],
})

export const BillCostMock = Factory.Sync.makeFactory<BillCost>({
  amount: Factory.each(() => faker.random.number().toString()),
  bill: Factory.each(() => faker.random.number()),
  cost: Factory.each(() => faker.random.number()),
  cost_allocation: CostAllocationMock.build(),
  currency: CurrencyMock.build().id,
  id: Factory.each(() => faker.random.number()),
  name: Factory.each(() => faker.finance.currencyCode()),
})

/**
 * Bill Status
 */

export const BillStatusBillUserMock = Factory.Sync.makeFactory<
  BillStatusBill['user']
>({
  id: Factory.each(() => faker.random.number()),
  firstName: Factory.each(() => faker.name.firstName()),
  lastName: Factory.each(() => faker.name.lastName()),
  email: Factory.each(() => faker.internet.email()),
  profile_image: Factory.each(() => faker.image.avatar()),
  branch_name: Factory.each(() => faker.name.firstName()),
  department_name: Factory.each(() => faker.name.firstName()),
})

export const BillStatusBillApproverMock =
  Factory.Sync.makeFactory<BillStatusBillApprover>({
    id: Factory.each(() => faker.random.number()),
    firstName: Factory.each(() => faker.name.firstName()),
    lastName: Factory.each(() => faker.name.lastName()),
    profile_image: Factory.each(() => faker.image.avatar()),
    approval_delegatee: null,
  })

export const BillStatusBillApprovalDelegateeMock =
  Factory.Sync.makeFactory<BillStatusBillApprovalDelegatee>({
    id: Factory.each(() => faker.random.number()),
    firstName: Factory.each(() => faker.name.firstName()),
    lastName: Factory.each(() => faker.name.lastName()),
  })

export const BillStatusBillMock = Factory.Sync.makeFactory<BillStatusBill>({
  id: Factory.each(() => faker.random.number()),
  creator: BillStatusBillUserMock.build(),
  currency: CurrencyMock.build(),
  due_date: Factory.each(() => faker.date.past().toISOString()),
  gl_post_date: Factory.each(() => faker.date.past().toISOString()),
  group: Factory.each(() => faker.random.number()),
  invoice_created: false,
  invoice_date: Factory.each(() => faker.date.past().toISOString()),
  invoice_uuid: null,
  invoice_number: Factory.each(() => faker.random.number().toString()),
  invoice_total: Factory.each(() => faker.random.number()),
  last_export_date: Factory.each(() => faker.date.past().toISOString()),
  last_export_user: Factory.each(() => UserMock.build()),
  last_modified_datetime: Factory.each(() => faker.date.past().toISOString()),
  locked: false,
  next_approver_choices: null,
  status: BillStatuses.DRAFT,
  submitted_date: Factory.each(() => faker.date.past().toISOString()),
  total_cost_with_tax: Factory.each(() => faker.random.number()),
  uuid: Factory.each(() => faker.random.uuid()),
  version: 1,
  type: BillTypes.ORDER,
  user: BillStatusBillUserMock.build(),
  vendor: SimpleVendorMock.build(),
  items: Factory.each(() => [faker.random.number()]),
  past_due: false,
  payment_uuid: null,
  approver: null,
  payment_date: null,
})
